import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ProductImageGallery = ({ product }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isZoomed, setIsZoomed] = useState(false);
  const [zoomPosition, setZoomPosition] = useState({ x: 0, y: 0 });

  // Helper function to construct full image URL
  const getImageUrl = (imagePath) => {
    if (!imagePath) return '/assets/images/no_image.png';

    // If it's already a full URL, return as is
    if (imagePath.startsWith('http')) {
      return imagePath;
    }

    // If it's a relative path, prepend the API base URL
    const baseUrl = import.meta.env?.VITE_API_BASE_URL || 'http://localhost:8000';
    return `${baseUrl}${imagePath}`;
  };

  const handleThumbnailClick = (index) => {
    setCurrentImageIndex(index);
    setIsZoomed(false);
  };

  const handleImageClick = () => {
    setIsZoomed(!isZoomed);
  };

  const handleMouseMove = (e) => {
    if (!isZoomed) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    setZoomPosition({ x, y });
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) =>
      prev === product?.images?.length - 1 ? 0 : prev + 1
    );
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) =>
      prev === 0 ? product?.images?.length - 1 : prev - 1
    );
  };

  return (
    <div className="space-y-4">
      {/* Main Image */}
      <div className="relative bg-accent-50 rounded-2xl overflow-hidden aspect-square">
        <div
          className="relative w-full h-full cursor-zoom-in"
          onClick={handleImageClick}
          onMouseMove={handleMouseMove}
          onMouseLeave={() => setIsZoomed(false)}
        >
          <Image
            src={getImageUrl(product?.images?.[currentImageIndex]?.image)}
            alt={product?.images?.[currentImageIndex]?.alt_text || product?.name || 'Product image'}
            className={`w-full h-full object-contain transition-all duration-300 ${isZoomed ? 'scale-150' : 'scale-100'
              }`}
            style={
              isZoomed
                ? {
                  transformOrigin: `${zoomPosition.x}% ${zoomPosition.y}%`,
                  padding: '8px',
                  maxWidth: '100%',
                  maxHeight: '100%'
                }
                : {
                  padding: '8px',
                  maxWidth: '100%',
                  maxHeight: '100%'
                }
            }
          />
        </div>

        {/* Navigation Arrows */}
        {product?.images?.length > 1 && (
          <>
            <button
              onClick={prevImage}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 w-10 h-10 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-soft transition-all duration-200 hover:scale-105"
            >
              <Icon name="ChevronLeft" size={20} className="text-accent-600" />
            </button>
            <button
              onClick={nextImage}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 w-10 h-10 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-soft transition-all duration-200 hover:scale-105"
            >
              <Icon name="ChevronRight" size={20} className="text-accent-600" />
            </button>
          </>
        )}

        {/* Zoom Indicator */}
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-accent-800/80 text-white px-3 py-1 rounded-full text-sm">
          <Icon name="ZoomIn" size={14} className="inline mr-1" />
          Click to zoom
        </div>
      </div>

      {/* Thumbnail Gallery */}
      {product?.images?.length > 1 && (
        <div className="flex space-x-2 overflow-x-auto">
          {product.images.map((image, index) => (
            <button
              key={index}
              onClick={() => handleThumbnailClick(index)}
              className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all duration-200 ${currentImageIndex === index
                ? 'border-primary shadow-primary/30'
                : 'border-accent-200 hover:border-accent-300'
                }`}
            >
              <Image
                src={getImageUrl(image.image)}
                alt={image.alt_text || `${product?.name} ${index + 1}`}
                className="w-full h-full object-contain"
                style={{
                  padding: '2px',
                  maxWidth: '100%',
                  maxHeight: '100%'
                }}
              />
            </button>
          ))}
        </div>
      )}

      {/* Image Details */}
      {product?.images?.[currentImageIndex]?.description && (
        <div className="text-center text-sm text-accent-600 italic">
          {product.images[currentImageIndex].description}
        </div>
      )}

      {/* Quality Badges */}
      <div className="flex justify-center space-x-4 text-xs text-accent-500">
        <div className="flex items-center">
          <Icon name="Shield" size={12} className="mr-1" />
          High Quality
        </div>
        <div className="flex items-center">
          <Icon name="Truck" size={12} className="mr-1" />
          Fast Shipping
        </div>
        <div className="flex items-center">
          <Icon name="RotateCcw" size={12} className="mr-1" />
          Easy Returns
        </div>
      </div>
    </div>
  );
};

export default ProductImageGallery;